/* English translations */
const en = {
  lang: 'en',
  langLabel: 'English',
  login: {
    welcome: 'Welcome back',
    subtitle: 'Sign in to your Atunears panel.',
    email: 'Email',
    emailPlaceholder: 'example@atunears.com',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password',
    submit: 'Sign In',
    submitting: 'Signing in…',
    noAccount: "Don't have an account?",
    contactUs: 'Please get in touch with us.',
    tagline: 'Personalized Audio Experience',
    heroTitle: '<em>Frequencies crafted </em>for your ears.<br/><em>Custom</em> headphones, just for you.',
    heroDesc: 'Manage your personal audio signature, order tracking, and ear mold appointments — all in one place.',
    feature1: 'Personal ear mold & audio signature',
    feature2: 'Handcrafted, custom shells',
    feature3: 'Order and appointment panel',
  },
  topbar: {
    adminPanel: 'Admin Panel',
    adminPanelSub: 'Manage users, forms, and assignments within your customer scope.',
    userPanel: 'User Panel',
    userPanelSub: 'Fill in your assigned forms and track your submission history.',
    refresh: 'Refresh',
    lightMode: 'Switch to light mode',
    darkMode: 'Switch to dark mode',
  },
  sidebarUser: {
    myForms: 'My Forms',
    newForm: 'New Form',
    sentForms: 'My Submissions',
  },
  sidebarAdmin: {
    formManagement: 'Form Management',
    addForm: 'Add Form',
    listForms: 'List Forms',
    moldTypes: 'Mold Management',
    users: 'Users',
    userManagement: 'User Management',
    data: 'Data',
    submissions: 'Submissions',
    kanban: 'Kanban',
  },
  userForms: {
    noMoldTitle: 'No mold types defined yet',
    noMoldDesc: 'When your administrator defines mold types, they will appear here automatically. Contact your administrator with any questions.',
    secureConnection: 'Secure connection',
    chooseMoldLabel: 'Mold selection',
    chooseMoldTitle: 'Choose the mold you want to fill',
    chooseMoldSub: (n) => `You can choose from ${n} different molds. Select one below to continue.`,
    selectedMoldLabel: 'Selected mold',
    moldList: 'Mold list',
    loadingForm: 'Loading form…',
    footerSecure: 'Your information is transmitted over an encrypted connection.',
    footerPlatform: 'Atunears Form Platform',
  },
  userSubmissions: {
    title: 'My Submissions',
    subtitle: 'Filter your submissions by form name, status, or date range.',
    formNameLabel: 'Form name',
    formNamePlaceholder: 'Search form',
    statusLabel: 'Status',
    all: 'All',
    dateFrom: 'From',
    dateTo: 'To',
    records: (n) => `${n} record${n !== 1 ? 's' : ''}`,
    noResults: 'No submissions match the current filters.',
    colForm: 'Form',
    colStatus: 'Status',
    colDate: 'Submitted',
    inspect: 'View',
    submittedAt: 'Submission date',
    downloadPdf: 'Download PDF',
    filesTitle: (n) => `Attached Files (${n})`,
    clearAllFiles: 'Clear All',
    clearing: 'Clearing…',
  },
  submissions: {
    title: 'Submissions',
    subtitle: 'Filter and review submissions in a table. Use the Kanban screen to change status.',
    records: (shown, total) => `${shown} / ${total} record${total !== 1 ? 's' : ''}`,
    filtersLabel: 'Filters',
    clearFilters: 'Clear',
    searchPlaceholder: 'Search by form, user, or status…',
    searchLabel: 'Search',
    dateFrom: 'From',
    dateTo: 'To',
    formsGroup: 'Forms',
    usersGroup: 'Users',
    statusesGroup: 'Statuses',
    colForm: 'Form',
    colUser: 'User',
    colStatus: 'Status',
    colSubmitted: 'Submitted',
    colUpdated: 'Updated',
    colActions: 'Actions',
    noResults: 'No submissions match the current filters.',
    download: 'Download',
    downloadPdf: 'Download PDF',
    clearFiles: 'Clear',
    inspect: 'View',
    noFiles: 'This submission has no downloadable files.',
    confirmClear: "All files of this submission will be permanently deleted from S3. Continue?",
    kanbanEmpty: 'Drop here',
    noStatuses: 'No statuses defined yet.',
    statusLabel: 'Status',
    filesTitle: (n) => `Attached Files (${n})`,
    clearAllFiles: 'Clear All',
    clearing: 'Clearing…',
    selectPlaceholder: 'Select form',
    userSelectPlaceholder: 'Select user',
    noFormOptions: 'No forms found',
    noUserOptions: 'No users found',
    searchInDropdown: 'Search…',
    noDropdownResults: 'No results',
    selectedCount: (n) => `${n} selected`,
    clearSelection: 'Clear selection',
    completedToggleGroup: 'Completed',
    showCompleted: 'Show completed',
    kanbanTitle: 'Order Kanban',
    kanbanSubtitle: 'Drag and drop orders between statuses. Completed orders are hidden by default.',
  },
  moldTypes: {
    title: 'Mold Management',
    subtitle: 'Upload the molds users choose before filling a form, and link their forms by language.',
    newMold: 'New Mold',
    newMoldDefaultName: 'New Mold',
    noMolds: 'No molds defined yet. Start with "New Mold".',
    selectMoldHint: 'Select a mold on the left to edit it.',
    uploadImage: 'Upload image',
    uploading: 'Uploading…',
    uploadError: 'An error occurred while uploading the image.',
    active: 'Active',
    inactive: 'Inactive',
    deleteMold: 'Delete mold',
    confirmDelete: (name) => `Delete the mold "${name}"? Linked forms won't be deleted, only unlinked.`,
    moldNameTitle: 'Mold Name by Language',
    moldNameHint: 'Users see the mold with the name in their own language. Turkish is required; English and German are optional.',
    moldNamePlaceholderRequired: 'e.g. Full Shell',
    moldNamePlaceholderOptional: 'Falls back to the Turkish name if left empty',
    required: 'required',
    languageFormsTitle: 'Forms by Language',
    languageFormsHint: 'Pick a separate form for each language. Turkish is recommended; English and German are optional.',
    recommended: 'recommended',
    pickForm: 'Select form',
    removeLink: 'Remove link',
  },
  common: {
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
  },
  sidebar: {
    logout: 'Log out',
    collapse: 'Collapse',
    expand: 'Expand',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },
  statuses: {
    Forwarded: 'Forwarded',
    InProduction: 'In Production',
    Shipped: 'Shipped',
  },
};

export default en;
